/* LP kit — shared behaviour. Loaded once by lp-theme.
   1. fade-in observer for .lp-fade
   2. anchor scrolling for [data-lp-target] (target "product" → first <lp-buy-box>); a [data-lp-handle] on the
      same link emits lp:product-change first, so the buy box shows that product when the scroll lands
   3. event bus: LP.emit / LP.on, events namespaced lp:*
   4. autoplay for [data-lp-autoplay] video only when motion is allowed
   6. LP.gsap(urls): GSAP on demand, the kit's one approved library. LP.near(el, fn): run fn once when el nears the
      viewport
   Rules: docs/lp-kit/00-component-standard.md §6. */
(function () {
  'use strict';
  if (window.LP) return;

  var root = document.documentElement;
  var motionOK = window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
  root.classList.add('lp-js');

  var LP = {
    emit: function (name, detail) {
      document.dispatchEvent(new CustomEvent('lp:' + name, { detail: detail || {}, bubbles: false }));
    },
    on: function (name, fn) {
      document.addEventListener('lp:' + name, fn);
      return function () { document.removeEventListener('lp:' + name, fn); };
    },
    resolveTarget: function (target) {
      if (!target) return null;
      var el = document.getElementById(target);
      if (!el && target === 'product') el = document.querySelector('lp-buy-box');
      return el;
    },
    /* what covers the top of the screen right now: the store header (sticky, hides on scroll) and a quick nav pinned
       to the top edge (lp-sticky-cta--top), whichever reaches lower */
    obstruction: function () {
      var y = 0, h = document.querySelector('.header'), b = document.querySelector('lp-sticky-cta.lp-sticky-cta--top:not([hidden])');
      if (h) y = Math.max(y, h.getBoundingClientRect().bottom);
      if (b) y = Math.max(y, b.getBoundingClientRect().bottom);
      return Math.max(0, y);
    },
    scrollTo: function (el) {
      if (!el) return;
      /* a target inside a hidden panel (e.g. a card in a tab not yet shown) has no box; land on its section instead */
      if (!el.getClientRects().length) { var sec = el.closest('.lp-section'); if (sec && sec.getClientRects().length) el = sec; else return; }
      /* The store header hides on any downward movement and returns on any upward one, so a landing is only
         predictable if the last movement is upward. Arrive from below: glide past the target, then come back up a
         short way. The header is then showing, a top quick nav sits under it, and the heading lands under both. */
      var self = this, headerH = parseFloat(getComputedStyle(root).getPropertyValue('--header-height')) || 0;
      var bar = document.querySelector('lp-sticky-cta.lp-sticky-cta--top'), barH = bar ? bar.offsetHeight : 0;
      var elTop = el.getBoundingClientRect().top + window.pageYOffset;
      var final = Math.max(0, Math.round(elTop - headerH - barH));
      var maxY = Math.max(0, root.scrollHeight - window.innerHeight);
      var overshoot = Math.min(maxY, final + 160);
      if (this.settleRaf) cancelAnimationFrame(this.settleRaf);
      if (this.settleTimer) clearTimeout(this.settleTimer);
      var behavior = motionOK ? 'smooth' : 'auto';
      /* wait until the page has moved and then stopped (or 1.2s), then run fn */
      var whenStill = function (fn) {
        var startY = window.pageYOffset, last = startY, still = 0, moved = false, t0 = performance.now();
        var tick = function () {
          var y = window.pageYOffset;
          if (Math.abs(y - startY) > 1) moved = true;
          if (!moved && performance.now() - t0 < 600) { self.settleRaf = requestAnimationFrame(tick); return; }
          if (Math.abs(y - last) < 1) still++; else still = 0;
          last = y;
          if (still < 4 && performance.now() - t0 < 1200) { self.settleRaf = requestAnimationFrame(tick); return; }
          fn();
        };
        self.settleRaf = requestAnimationFrame(tick);
      };
      var land = function () {
        window.scrollTo({ top: final, behavior: behavior });
        /* the header slides back over ~300ms; then measure what covers the top and make one small final nudge */
        whenStill(function () {
          self.settleTimer = setTimeout(function () {
            var diff = Math.round(el.getBoundingClientRect().top - self.obstruction());
            if (Math.abs(diff) > 2 && Math.abs(diff) < 90) window.scrollTo({ top: window.pageYOffset + diff, behavior: 'instant' });
          }, 350);
        });
      };
      if (overshoot > final && overshoot !== window.pageYOffset) { window.scrollTo({ top: overshoot, behavior: behavior }); whenStill(land); }
      else land();
    },
    motionOK: motionOK,
    /* colours that read on a given hex surface (01/10/2026): text (white on a dark colour, a deep shade of the hue
       on a light one), accent (a lifted or deepened shade of the hue for highlight words and links) and halo (the
       opposite tone of the text, for text shadows). Plain sRGB maths so every browser agrees; no CSS colour functions. */
    onHex: function (hex) {
      var m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(hex || '').trim()); if (!m) return null;
      var h = m[1]; if (h.length === 3) h = h.replace(/./g, function (c) { return c + c; });
      var r = parseInt(h.slice(0, 2), 16) / 255, g = parseInt(h.slice(2, 4), 16) / 255, b = parseInt(h.slice(4, 6), 16) / 255;
      var lum = 0.2126 * r + 0.7152 * g + 0.0722 * b, light = lum > 0.45;
      var max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2, d = max - min, hue = 0, sat = 0;
      if (d) { sat = d / (1 - Math.abs(2 * l - 1)); hue = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4; hue = (hue * 60 + 360) % 360; }
      var hsl = function (ll, ss) { return 'hsl(' + hue.toFixed(1) + ' ' + Math.round(Math.min(1, ss) * 100) + '% ' + Math.round(ll * 100) + '%)'; };
      return light
        ? { text: hsl(0.18, sat * 0.9), accent: hsl(0.26, sat), halo: 'rgba(255,255,255,0.75)', light: true }
        : { text: '#ffffff', accent: hsl(0.84, Math.max(sat, 0.5)), halo: 'rgba(0,0,0,0.55)', light: false };
    }
  };
  window.LP = LP;

  /* 1. fade-in */
  function initFade(scope) {
    var items = (scope || document).querySelectorAll('.lp-fade:not(.is-visible)');
    if (!items.length) return;
    if (!motionOK || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });
    items.forEach(function (el) { io.observe(el); });
  }

  /* 2. anchor scroll */
  document.addEventListener('click', function (ev) {
    var a = ev.target.closest('[data-lp-target]');
    if (!a) return;
    var el = LP.resolveTarget(a.getAttribute('data-lp-target'));
    if (!el) return;
    ev.preventDefault();
    var handle = a.getAttribute('data-lp-handle');
    if (handle) LP.emit('product-change', { handle: handle, source: 'lp-anchor' });
    LP.scrollTo(el);
  });

  /* 4. autoplay only when motion is allowed and a poster is present */
  function initVideo(scope) {
    (scope || document).querySelectorAll('video[data-lp-autoplay]').forEach(function (v) {
      if (!motionOK || !v.getAttribute('poster')) return;
      if (v.dataset.lpReady) return;
      v.dataset.lpReady = '1';
      if (!('IntersectionObserver' in window)) { v.play().catch(function () {}); return; }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { e.target.play().catch(function () {}); }
          else { e.target.pause(); }
        });
      }, { threshold: 0.25 });
      io.observe(v);
    });
  }

  function init(scope) { initFade(scope); initVideo(scope); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();

  /* theme editor: re-run when a section is (re)loaded */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  LP.init = init;

  /* 6. GSAP on demand (standard §6): vendored as assets/lp-vendor-gsap*.js, never from a CDN. A section passes the
        asset urls (core first, then plugins) and must work without GSAP. Resolves with window.gsap, or null when
        motion is reduced or a file fails. Each url is fetched once per page, however many sections ask. */
  var scripts = {};
  function loadScript(src) {
    if (!scripts[src]) {
      scripts[src] = new Promise(function (resolve, reject) {
        var el = document.createElement('script');
        el.src = src; el.async = true;
        el.onload = resolve; el.onerror = reject;
        document.head.appendChild(el);
      });
    }
    return scripts[src];
  }
  LP.gsap = function (urls) {
    urls = (urls || []).filter(Boolean);
    if (!LP.motionOK || !urls.length) return Promise.resolve(null);
    return loadScript(urls[0])
      .then(function () { return Promise.all(urls.slice(1).map(loadScript)); })
      .then(function () {
        var g = window.gsap;
        if (!g) return null;
        if (window.Flip) g.registerPlugin(window.Flip);
        return g;
      })
      .catch(function () { return null; });
  };
  /* fn runs once, when el comes within `margin` of the viewport (default 1.5 screens) */
  LP.near = function (el, fn, margin) {
    if (!('IntersectionObserver' in window)) { fn(); return function () {}; }
    var io = new IntersectionObserver(function (entries) {
      if (!entries.some(function (e) { return e.isIntersecting; })) return;
      io.disconnect(); fn();
    }, { rootMargin: (margin || '150%') + ' 0px' });
    io.observe(el);
    return function () { io.disconnect(); };
  };

  /* 5. <lp-countdown> — ends-at is AEST (UTC+10) unless the value carries an offset. Hidden until first tick.
        data-expired: text shown when over (blank = hide); data-expired-action="hide-section" hides the parent section. */
  if (!window.customElements.get('lp-countdown')) {
  function parseAEST(v) {
    v = (v || '').trim();
    if (!v) return NaN;
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) v += 'T23:59:59';
    if (!/([zZ]|[+-]\d{2}:?\d{2})$/.test(v)) v += '+10:00';
    return new Date(v).getTime();
  }

  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  class LpCountdown extends HTMLElement {
    connectedCallback() {
      this.ends = parseAEST(this.dataset.ends);
      this.out = this.querySelector('[data-lp-time]');
      if (isNaN(this.ends) || !this.out) { this.remove(); return; }
      this.tick();
      this.timer = setInterval(this.tick.bind(this), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      var diff = this.ends - Date.now();
      if (diff <= 0) {
        clearInterval(this.timer);
        if (this.dataset.expiredAction === 'hide-section') { var sec = this.closest('.lp-section'); if (sec) sec.hidden = true; return; }
        var expired = this.dataset.expired;
        if (!expired) { this.hidden = true; return; }
        var label = this.querySelector('[data-lp-countdown-label]');
        if (label) label.remove();
        this.out.textContent = expired;
        this.hidden = false;
        return;
      }
      var d = Math.floor(diff / 864e5), h = Math.floor(diff / 36e5) % 24, m = Math.floor(diff / 6e4) % 60, s = Math.floor(diff / 1e3) % 60;
      this.out.textContent = (d > 0 ? d + 'd ' : '') + pad(h) + ':' + pad(m) + ':' + pad(s);
      this.hidden = false;
    }
  }
    window.customElements.define('lp-countdown', LpCountdown);
  }
})();
